import { useAuthenticate } from '@account-kit/react'
import { useConnectModal } from '@rainbow-me/rainbowkit'
import { type ChangeEvent, type FC, memo, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import BaseModal from 'components/BaseModal'
import { TransactionButton } from 'components/Button/Button'
import Input from 'components/Input/Input'
import Spacer from 'components/Spacer'
import { Body } from 'components/Text'
import { FlexDivCol, FlexDivColCentered, FlexDivRowCentered } from 'components/layout/flex'
import Connector from 'containers/Connector'
import { setOpenModal } from 'state/app/reducer'
import { useAppDispatch, useAppSelector } from 'state/hooks'
import { setEmailAddress } from 'state/wallet/reducer'
import { selectEmailAddress } from 'state/wallet/selectors'
import media from 'styles/media'

type Props = {
	onDismiss(): void
}

const Separator = memo(() => {
	const { t } = useTranslation()
	return (
		<DividerContainer>
			<StyledBody size="small" color="secondary">
				{t('account.login-modal.or')}
			</StyledBody>
			<Divider height={1} divider />
		</DividerContainer>
	)
})

const Login: FC<Props> = memo(({ onDismiss }) => {
	const { t } = useTranslation()
	const dispatch = useAppDispatch()
	const { isWalletConnected, alchemySigner } = Connector.useContainer()
	const { openConnectModal } = useConnectModal()
	const emailAddress = useAppSelector(selectEmailAddress)

	const onAddressChange = useCallback(
		(e: ChangeEvent<HTMLInputElement>) => dispatch(setEmailAddress(e.target.value)),
		[dispatch]
	)
	const { authenticate } = useAuthenticate()

	const handleConnectWallet = useCallback(() => {
		openConnectModal?.()
		dispatch(setOpenModal(null))
	}, [dispatch, openConnectModal])

	const isEmailValid = useMemo(
		() => emailAddress && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailAddress),
		[emailAddress]
	)

	const socialAuth = useCallback(() => {
		if (!emailAddress) return
		authenticate({
			email: emailAddress,
			type: 'email',
			redirectParams: new URLSearchParams({ callback: origin }),
		})
	}, [authenticate, emailAddress])

	const handleSignInByEmail = useCallback(() => {
		socialAuth()
		dispatch(setOpenModal('email_verification'))
	}, [dispatch, socialAuth])

	const handleUsePasskey = useCallback(() => {
		alchemySigner?.authenticate({
			type: 'passkey',
			createNew: false,
		})
		onDismiss()
	}, [onDismiss, alchemySigner])

	return (
		<StyledBaseModal title={t('account.login-modal.title')} isOpen onDismiss={onDismiss}>
			<Spacer height={20} />
			<FlexDivCol rowGap="20px">
				<FlexDivCol rowGap="10px">
					<Body size="small" color="secondary">
						{t('account.login-modal.enter-your-email')}
					</Body>
					<StyledInput
						value={emailAddress}
						onChange={onAddressChange}
						placeholder={t('account.login-modal.email-address')}
					/>
				</FlexDivCol>
				<TransactionButton
					variant="solid-highlight"
					fullWidth
					disabled={!isEmailValid}
					onClick={handleSignInByEmail}
				>
					{t('account.login-modal.continue')}
				</TransactionButton>
				<Separator />
				<FlexDivRowCentered columnGap="10px">
					<TransactionButton fullWidth onClick={handleConnectWallet} disabled={!!isWalletConnected}>
						{t('account.login-modal.connect-wallet')}
					</TransactionButton>
					<TransactionButton variant="solid-highlight" fullWidth onClick={handleUsePasskey}>
						{t('account.login-modal.use-passkey')}
					</TransactionButton>
				</FlexDivRowCentered>
			</FlexDivCol>
		</StyledBaseModal>
	)
})

const DividerContainer = styled(FlexDivColCentered)`
	position: relative;
`

const StyledBody = styled(Body)`
	position: absolute;
	top: -1px;
	padding: 0 12px;
	background-color: ${(props) => props.theme.colors.selectedTheme.background};
`

const Divider = styled(Spacer)`
	margin: 5px 0;
	width: 100%;
`

const StyledInput = styled(Input)`
	font-size: 15px;
	font-weight: 400;
	font-feature-settings: 'tnum';

	${media.lessThan('md')`
		font-size: 13px;
	`}
`

const StyledBaseModal = styled(BaseModal)`
	[data-reach-dialog-content] {
		width: 400px;
		margin-top: 200px;
	}
`

export default Login
